import { Group, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { resetRelatedTasksTabState } from '../../../store/strategy-store/strategy-store.actions';
import { primaryBase, secondaryBase, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { Label } from '../initiative-list/filters/status-label';
import type { StrategyEntity, StrategyEntityType } from '../strategy.type';
import { calculatePercentProgress } from './strategy-entity-cells.helpers';
import { getSourceIcon, getStatusColor } from './strategy-entity-table.helpers';

// Re-export StrategyEntity for convenience

type EntityCellProps = {
  entity: StrategyEntity;
  entityType: StrategyEntityType;
};

/**
 * Cell component for displaying entity ID with status indicator and source icon
 */
export const EntityIDCell = ({ entity, entityType }: EntityCellProps) => {
  const navigate = useNavigate();
  const portfolio = useGlobalStore((state) => state.portfolio);

  if (!portfolio) {
    return null;
  }

  const key = (entity as any).key || (entity as any).title || 'Unknown Key';
  const source = (entity as any).source || 'bloomfilter';

  // Get status from the entity
  const status = (entity as any).status?.by_tasks?.status || undefined;

  // Determine the navigation path based on entity type
  const getNavigationPath = () => {
    switch (entityType) {
      case 'initiative':
        return `/application/strategy/${portfolio.id}/${entity.id}`;
      case 'goal':
        return `/application/strategy/goals/${entity.id}`;
      case 'outcome':
        return `/application/strategy/outcomes/${entity.id}`;
      case 'epic':
        return `/application/strategy/epics/${entity.id}`;
      default:
        return `/application/strategy/${portfolio.id}/${entity.id}`;
    }
  };

  return (
    <Label color={getStatusColor(status)}>
      <Text
        size="small"
        lineHeight="none"
        color={secondaryBase}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={(e) => {
          e.stopPropagation();
          if (entityType === 'initiative') {
            resetRelatedTasksTabState();
          }
          navigate(getNavigationPath());
        }}
      >
        {key}
      </Text>
      {entityType !== 'epic' && <img src={getSourceIcon(source)} width={16} height={16} alt={source} />}
    </Label>
  );
};

/**
 * Cell component for displaying completion percentage with progress bar
 */
export const EntityCompletionCell = ({ entity }: { entity: StrategyEntity }) => {
  const percentProgress = calculatePercentProgress(entity);
  const progressWidth = Math.min(percentProgress, 100);

  return (
    <Stack gap={1} mb={4}>
      <Group justify="space-between" gap={0}>
        <Text size="small">{Math.round(percentProgress)}%</Text>
        {(entity as any).subtasks_count !== undefined && (
          <Text size="small">
            {(entity as any).subtasks_completed_count}/{(entity as any).subtasks_count}
          </Text>
        )}
      </Group>
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: skyLight,
          borderRadius: '2px',
        }}
      >
        <div
          style={{
            width: `${progressWidth}%`,
            height: '100%',
            backgroundColor: primaryBase,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </Stack>
  );
};

/**
 * Cell component for displaying end date with no_end_date indicator
 */
export const EntityEndDateCell = ({ entity }: { entity: StrategyEntity }) => {
  const i18n = useTranslation();
  const endDate = (entity as any).end_date;
  const noEndDate = (entity as any).no_end_date;

  return (
    <Group gap={4} justify="space-between" wrap="nowrap">
      <Text size="small" style={{ whiteSpace: 'nowrap' }}>
        {formatDate(endDate, { fallback: i18n.t('strategy.initiatives.no_date') })}
      </Text>
      {noEndDate && (
        <Tooltip
          variant="white"
          label={i18n.t('initiatives.section_status.no_end_date')}
          position="top-start"
          multiline
          style={{
            maxWidth: 262,
            lineHeight: '20px',
          }}
        >
          <Icon name="motion_photos_auto" size={16} pointer style={{ marginBottom: 2 }} />
        </Tooltip>
      )}
    </Group>
  );
};
